import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ChevronDownIcon, ChevronUpIcon, XMarkIcon, UserPlusIcon,
  PlusIcon, CheckCircleIcon, ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { documentalApi } from '../../api/documental';

const DEFAULT_FIELDS = [
  'TELEFONO_DE_LA_EMPRESA',
  'CORREO_EMPRESARIAL',
  'CALLEDEL_NUEVO_DOMICILIO',
  'SECTORDEL_NUEVO_DOMICILIO',
  'CIUDADDEL_NUEVO_DOMICILIO',
  'PROVINCIADEL_NUEVO_DOMICILIO',
  'ACTIVIDAD_COMERCIAL',
  'FECHA_ACTA',
  'CIUDAD_ACTA',
  'HORA_ACTA',
];

const FIELD_LABELS = {
  TELEFONO_DE_LA_EMPRESA: 'Teléfono de la empresa',
  CORREO_EMPRESARIAL: 'Correo empresarial',
  CALLEDEL_NUEVO_DOMICILIO: 'Calle del nuevo domicilio',
  SECTORDEL_NUEVO_DOMICILIO: 'Sector del nuevo domicilio',
  CIUDADDEL_NUEVO_DOMICILIO: 'Ciudad del nuevo domicilio',
  PROVINCIADEL_NUEVO_DOMICILIO: 'Provincia del nuevo domicilio',
  ACTIVIDAD_COMERCIAL: 'Actividad comercial',
  FECHA_ACTA: 'Fecha del acta',
  CIUDAD_ACTA: 'Ciudad del acta',
  HORA_ACTA: 'Hora del acta',
};

const SectionCard = ({ title, expanded, onToggle, children, badge }) => (
  <div className="border border-gray-200 rounded-xl overflow-hidden">
    <button
      type="button"
      onClick={onToggle}
      className="flex items-center justify-between w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
    >
      <span className="text-sm font-semibold text-gray-800 flex items-center gap-2">
        {title}
        {badge && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700">{badge}</span>
        )}
      </span>
      {expanded
        ? <ChevronUpIcon className="h-4 w-4 text-gray-500" />
        : <ChevronDownIcon className="h-4 w-4 text-gray-500" />}
    </button>
    {expanded && <div className="p-4">{children}</div>}
  </div>
);

export default function ClienteCambiosDocumental() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [expediente, setExpediente] = useState(null);
  const [form, setForm] = useState({});
  const [step, setStep] = useState('edit');
  const [sections, setSections] = useState({
    datos: true,
    socios: true,
    orden_dia: true,
  });

  const toggleSection = (s) => setSections((p) => ({ ...p, [s]: !p[s] }));

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await documentalApi.getClienteExpedienteByToken(token);
        setExpediente(res);
        setForm(res?.datos_cambios || {});
        if (res?.cambios_confirmados_cliente) setStep('confirmado');
      } catch (e) {
        setError(e?.response?.data?.detail || e.message || 'No se pudo cargar el enlace');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const visibleFields = useMemo(() => {
    const schema = expediente?.tipo_tramite?.schema_campos;
    const configured = Array.isArray(schema?.fields) ? schema.fields : [];
    return configured.length > 0 ? configured : DEFAULT_FIELDS;
  }, [expediente]);

  const socios = useMemo(() => Array.isArray(form.socios_modificados) ? form.socios_modificados : [], [form]);
  const ordenDia = useMemo(() => Array.isArray(form.orden_dia) ? form.orden_dia : [], [form]);

  const updateSocio = (i, field, val) => {
    const s = [...socios];
    s[i] = { ...s[i], [field]: val };
    setForm((p) => ({ ...p, socios_modificados: s }));
  };
  const addSocio = () => setForm((p) => ({ ...p, socios_modificados: [...socios, { nombre: '', apellido: '', no_documento: '', participacion: '', cargo: '' }] }));
  const removeSocio = (i) => setForm((p) => ({ ...p, socios_modificados: socios.filter((_, idx) => idx !== i) }));

  const updateOrdenDia = (i, patch) => {
    const list = [...ordenDia];
    list[i] = { ...list[i], ...patch };
    setForm((p) => ({ ...p, orden_dia: list }));
  };
  const addOrdenDia = () => setForm((p) => ({ ...p, orden_dia: [...ordenDia, { texto: '', activo: true }] }));
  const removeOrdenDia = (i) => setForm((p) => ({ ...p, orden_dia: ordenDia.filter((_, idx) => idx !== i) }));

  const handleSaveDraft = async () => {
    try {
      setSaving(true);
      setError('');
      await documentalApi.guardarCambiosCliente(token, form);
      setOk('Borrador guardado correctamente. Puedes continuar editando.');
      setTimeout(() => setOk(''), 4000);
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = async () => {
    try {
      setConfirming(true);
      setError('');
      await documentalApi.confirmarCambiosCliente(token, form);
      setStep('confirmado');
      setShowConfirmModal(false);
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo confirmar');
      setShowConfirmModal(false);
    } finally {
      setConfirming(false);
    }
  };

  // Calcular horas restantes del link
  const horasRestantes = useMemo(() => {
    if (!expediente?.link_cliente_expira_en) return null;
    const diff = new Date(expediente.link_cliente_expira_en) - new Date();
    return Math.max(0, Math.round(diff / (1000 * 60 * 60)));
  }, [expediente]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Cargando...</div>
      </div>
    );
  }

  if (!expediente && error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-xl border border-red-200 shadow-sm p-8 text-center max-w-md w-full">
          <ExclamationTriangleIcon className="h-10 w-10 text-red-400 mx-auto mb-3" />
          <p className="text-red-700 font-semibold">{error}</p>
        </div>
      </div>
    );
  }

  if (expediente?.link_cliente_expirado) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-xl border border-red-200 shadow-sm p-8 text-center max-w-md w-full">
          <ExclamationTriangleIcon className="h-10 w-10 text-red-400 mx-auto mb-3" />
          <p className="text-red-700 font-semibold text-lg">Este enlace ha expirado</p>
          <p className="text-gray-600 text-sm mt-2">
            Solicita a tu asesor que te comparta un nuevo enlace actualizado.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Solicitud de cambios</p>
              <h1 className="text-xl font-bold text-gray-900">{expediente.empresa_nombre}</h1>
              {expediente.empresa_rnc && (
                <p className="text-sm text-gray-500 mt-0.5">RNC: {expediente.empresa_rnc}</p>
              )}
              <p className="text-sm text-gray-600 mt-1">
                Trámite: <span className="font-medium">{expediente.tipo_tramite_nombre}</span>
              </p>
            </div>
            <div className="text-right shrink-0">
              {step === 'confirmado' ? (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                  <CheckCircleIcon className="h-3.5 w-3.5" />
                  Confirmado
                </span>
              ) : (
                <span className="inline-flex px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                  Pendiente de confirmación
                </span>
              )}
              {horasRestantes !== null && horasRestantes < 24 && step !== 'confirmado' && (
                <p className="text-xs text-red-500 mt-1">
                  ⚠ Link expira en {horasRestantes}h
                </p>
              )}
            </div>
          </div>
        </div>

        {step === 'confirmado' ? (
          <div className="bg-white rounded-xl border border-emerald-200 shadow-sm p-8 text-center">
            <CheckCircleIcon className="h-14 w-14 text-emerald-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-emerald-700">¡Cambios confirmados!</h2>
            <p className="text-gray-600 text-sm mt-2 max-w-sm mx-auto">
              Hemos recibido tu confirmación. Nuestro equipo revisará los cambios y generará el documento final.
              Este formulario queda bloqueado.
            </p>
          </div>
        ) : (
          <>
            {/* Formulario en secciones */}
            <div className="space-y-4">
              {/* Datos del trámite */}
              <SectionCard
                title="Datos del trámite"
                expanded={sections.datos}
                onToggle={() => toggleSection('datos')}
                badge={`${visibleFields.filter((k) => form[k]).length}/${visibleFields.length} completados`}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {visibleFields.map((key) => (
                    <div key={key}>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {FIELD_LABELS[key] || key.replaceAll('_', ' ').toLowerCase()}
                      </label>
                      <input
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                        value={form[key] || ''}
                        onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))}
                      />
                    </div>
                  ))}
                </div>
              </SectionCard>

              {/* Socios */}
              <SectionCard
                title="Socios / Accionistas"
                expanded={sections.socios}
                onToggle={() => toggleSection('socios')}
                badge={`${socios.length}`}
              >
                {socios.length === 0 && (
                  <p className="text-sm text-gray-400 italic mb-3">Sin socios registrados</p>
                )}
                <div className="space-y-3">
                  {socios.map((s, i) => (
                    <div key={i} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-gray-600">Socio {i + 1}</span>
                        <button type="button" onClick={() => removeSocio(i)} className="text-red-400 hover:text-red-600">
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          ['nombre', 'Nombre'],
                          ['apellido', 'Apellido'],
                          ['no_documento', 'Documento'],
                          ['participacion', 'Cuotas/Participación'],
                          ['cargo', 'Cargo'],
                          ['firma_nombre', 'Nombre en firma'],
                        ].map(([field, label]) => (
                          <div key={field}>
                            <label className="block text-xs text-gray-500 mb-0.5">{label}</label>
                            <input
                              className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm"
                              value={s[field] || ''}
                              onChange={(e) => updateSocio(i, field, e.target.value)}
                            />
                          </div>
                        ))}
                        <div className="col-span-2">
                          <label className="block text-xs text-gray-500 mb-0.5">Dirección</label>
                          <input
                            className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm"
                            value={s.direccion || ''}
                            onChange={(e) => updateSocio(i, 'direccion', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={addSocio}
                  className="mt-3 flex items-center gap-1 text-sm text-primary-600 hover:text-primary-800"
                >
                  <UserPlusIcon className="h-4 w-4" />
                  Añadir socio
                </button>
              </SectionCard>

              {/* Orden del día */}
              <SectionCard
                title="Orden del día"
                expanded={sections.orden_dia}
                onToggle={() => toggleSection('orden_dia')}
                badge={`${ordenDia.filter((i) => i.activo !== false).length} puntos activos`}
              >
                {ordenDia.length === 0 && (
                  <p className="text-sm text-gray-400 italic mb-3">Sin puntos del orden del día</p>
                )}
                <div className="space-y-2">
                  {ordenDia.map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        className="accent-primary-600 shrink-0"
                        checked={item.activo !== false}
                        onChange={(e) => updateOrdenDia(i, { activo: e.target.checked })}
                      />
                      <input
                        className={`flex-1 border rounded-lg px-3 py-1.5 text-sm ${item.activo !== false ? 'border-gray-300' : 'border-gray-200 bg-gray-50 text-gray-400 line-through'}`}
                        placeholder={`Punto ${i + 1}`}
                        value={item.texto || ''}
                        onChange={(e) => updateOrdenDia(i, { texto: e.target.value })}
                      />
                      <button type="button" onClick={() => removeOrdenDia(i)} className="text-red-400 hover:text-red-600 shrink-0">
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={addOrdenDia}
                  className="mt-3 flex items-center gap-1 text-sm text-primary-600 hover:text-primary-800"
                >
                  <PlusIcon className="h-4 w-4" />
                  Agregar punto
                </button>
              </SectionCard>
            </div>

            {/* Mensajes */}
            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>
            )}
            {ok && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">{ok}</div>
            )}

            {/* Acciones */}
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3 bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
              <p className="text-xs text-gray-500">
                Guarda el borrador para volver más tarde, o confirma cuando estés listo.
              </p>
              <div className="flex items-center gap-3 shrink-0">
                <button
                  onClick={handleSaveDraft}
                  disabled={saving}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {saving ? 'Guardando...' : 'Guardar borrador'}
                </button>
                <button
                  onClick={() => setShowConfirmModal(true)}
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium"
                >
                  Confirmar cambios
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Modal de confirmación */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Confirmar cambios</h3>
            <p className="text-sm text-gray-600 mb-4">
              Al confirmar, los cambios serán enviados a PS Asesores & Consultores y no podrás editarlos de nuevo.
              ¿Deseas continuar?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                disabled={confirming}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirm}
                disabled={confirming}
                className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
              >
                {confirming ? 'Confirmando...' : 'Sí, confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
